<script setup lang="ts">
// Add complete title with prefix and specify ogTitle
useSeoMeta({
  title: 'AERO.NOW • Privacy Policy',
  ogTitle: 'AERO.NOW • Privacy Policy',
})

const currentDate = new Date().toISOString().split('T')[0]
</script>

<template>
  <UContainer>
    <UPage>
      <ULandingSection icon="i-heroicons-shield-check" class="py-6">
        <template #title>
          <h1 class="uppercase mb-1">Privacy Policy</h1>
        </template>

        <div class="text-gray-500 dark:text-gray-400 mb-8 text-center -mt-16">
          Effective Date: {{ currentDate }}
        </div>

        <div class="prose prose-primary dark:prose-invert max-w-none">
          <section class="mb-6">
            <h2 class="text-xl font-semibold">1. Overview</h2>
            <p>
              This Privacy Policy explains how AERO.NOW collects, uses, and
              protects your information when you subscribe or visit our website.
              This policy should be read alongside our
              <NuxtLink
                to="/terms"
                class="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Terms of Service </NuxtLink
              >.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">2. Information Controller</h2>
            <p>
              AERO.NOW is the data controller responsible for your personal
              information. If you have any questions about this Privacy Policy
              or our data practices, please contact us at
              <a
                href="mailto:bart@aero.now"
                class="text-primary-600 dark:text-primary-400 hover:underline"
              >
                bart@aero.now</a
              >.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">3. What We Collect</h2>
            <ul class="list-disc list-inside ml-4">
              <li>Email address (for newsletter delivery)</li>
              <li>
                Usage data (e.g., IP address, browser info) via PostHog
                (EU-hosted)
              </li>
              <li>
                Interaction data (e.g., open rates, clicks), subject to cookie
                consent
              </li>
              <li>
                Any information you voluntarily provide through communications
                with us
              </li>
            </ul>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">
              4. How We Use Your Information
            </h2>
            <ul class="list-disc list-inside ml-4">
              <li>To provide, maintain, and improve our services</li>
              <li>To send newsletters and communications you've requested</li>
              <li>To analyze engagement and improve content</li>
              <li>To prevent fraud and protect our service</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">5. Cookie Usage</h2>
            <p>
              We use cookies to track performance and engagement, subject to
              your consent. For visitors from the European Union, European
              Economic Area, United Kingdom, and Switzerland, no cookies are set
              until you explicitly accept via the cookie banner.
            </p>
            <p class="mt-2">
              For visitors from outside these regions, performance and analytics
              cookies may be enabled by default to improve our service. However,
              you can always manage your cookie preferences through your browser
              settings or by contacting us.
            </p>
            <p class="mt-2">
              You can control cookies through your browser settings and the
              preference center available on our site. However, disabling
              cookies may limit your ability to use certain features of our
              service.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">6. Legal Bases</h2>
            <p>
              Under GDPR and similar regulations, we process your data based on:
            </p>
            <ul class="list-disc list-inside ml-4">
              <li>
                <strong>Consent</strong>: When you explicitly agree to the
                processing of your data for specific purposes
              </li>
              <li>
                <strong>Legitimate interest</strong>: When processing is
                necessary for our legitimate interests, such as improving our
                service or preventing fraud
              </li>
              <li>
                <strong>Contractual necessity</strong>: When processing is
                needed to deliver the service you've requested
              </li>
              <li>
                <strong>Legal obligation</strong>: When we need to process data
                to comply with a legal requirement
              </li>
            </ul>
            <p class="mt-2">
              You may withdraw consent at any time by contacting us.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">7. Data Retention</h2>
            <p>
              We retain your data only as long as necessary for service
              delivery, or as legally required. You may request deletion at any
              time by contacting us at
              <a
                href="mailto:bart@aero.now"
                class="text-primary-600 dark:text-primary-400 hover:underline"
              >
                bart@aero.now</a
              >.
            </p>
            <p class="mt-2">
              In some cases, we may anonymize your personal information for
              analytical purposes, in which case we may use this anonymized data
              indefinitely without further notice.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">8. Your Rights</h2>
            <p>Depending on your jurisdiction, you have the right to:</p>
            <ul class="list-disc list-inside ml-4">
              <li>Access, correct, or delete your personal data</li>
              <li>Withdraw consent for data processing</li>
              <li>Restrict or object to certain processing activities</li>
              <li>Request data portability</li>
              <li>File a complaint with a regulatory authority</li>
              <li>
                Not be subject to automated decision-making, including profiling
              </li>
            </ul>
            <p class="mt-2">
              To exercise any of these rights, please contact us at
              <a
                href="mailto:bart@aero.now"
                class="text-primary-600 dark:text-primary-400 hover:underline"
              >
                bart@aero.now</a
              >. We will respond to your request within 30 days.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">9. Sharing and Disclosure</h2>
            <p>
              We do not sell your data. We share it only with service providers
              necessary for delivery (e.g., analytics, email platforms), bound
              by data processing agreements.
            </p>
            <p class="mt-2">
              We may also disclose your information if required to do so by law
              or in response to valid requests by public authorities (e.g., a
              court or government agency).
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">10. International Users</h2>
            <p>
              We use EU-based infrastructure (e.g., PostHog Cloud EU). If you
              are outside the EU, data may be transferred to your region as
              needed.
            </p>
            <p class="mt-2">
              By using our service, you consent to the transfer of information
              to countries outside your country of residence, which may have
              different data protection rules than those of your country. When
              we transfer data internationally, we take steps to ensure your
              data is treated securely and in accordance with this Privacy
              Policy.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">11. Children's Privacy</h2>
            <p>
              Our service is not intended for use by anyone under the age of 13.
              We do not knowingly collect personally identifiable information
              from children under 13. If you are a parent or guardian and
              believe we may have collected information about a child under 13,
              please contact us so that we can delete that information.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">12. Updates to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will
              notify you of any material changes by posting the new Privacy
              Policy on this page and updating the "Effective Date" at the top.
              You are advised to review this Privacy Policy periodically for any
              changes.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">13. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy,
              please contact:

              <a
                href="mailto:bart@aero.now"
                class="text-primary-600 dark:text-primary-400 hover:underline"
              >
                bart@aero.now
              </a>
            </p>
          </section>
        </div>
      </ULandingSection>
    </UPage>
  </UContainer>
</template>
